import { BillingAction, ClientBilling, CollectionMethod } from "../../types";

interface BillingMethodPickerProps {
  /** Which endpoint confirm will hit. */
  action: BillingAction;
  /** How a charge_card confirm collects; unused while scheduling a notice. */
  collectionMethod: CollectionMethod;
  onSelect: (action: BillingAction, method: CollectionMethod) => void;
  canAutoCharge: boolean;
  canSendInvoice: boolean;
  /** The notice ends in a card charge, so it needs a card as well as a Stripe
   *  customer — the same predicate as canAutoCharge, named for what it gates. */
  canScheduleNotice: boolean;
  paymentMethod: ClientBilling["paymentMethod"];
  dueDaysStr: string;
  onDueDaysChange: (value: string) => void;
  isDueDaysValid: boolean;
  noticeDaysStr: string;
  onNoticeDaysChange: (value: string) => void;
  isNoticeDaysValid: boolean;
}

export function BillingMethodPicker({
  action,
  collectionMethod,
  onSelect,
  canAutoCharge,
  canSendInvoice,
  canScheduleNotice,
  paymentMethod: pm,
  dueDaysStr,
  onDueDaysChange,
  isDueDaysValid,
  noticeDaysStr,
  onNoticeDaysChange,
  isNoticeDaysValid,
}: BillingMethodPickerProps) {
  const isScheduleNotice = action === "schedule_notice";
  const isSendInvoice =
    action === "charge_card" && collectionMethod === "send_invoice";
  const isChargeNow =
    action === "charge_card" && collectionMethod === "charge_automatically";

  // Quoted in the sub-copy, so an unparseable box never reads "in NaN days".
  const noticeDays = parseInt(noticeDaysStr, 10);
  const noticeDaysLabel = isNoticeDaysValid
    ? `${noticeDays} day${noticeDays === 1 ? "" : "s"}`
    : "a few days";

  return (
    <div>
      <div className="text-sm font-medium text-content-primary mb-1.5">
        Billing Method
      </div>
      <div className="space-y-2">
        <label
          className={`flex items-start gap-2 text-sm rounded-lg border p-3 ${
            isChargeNow
              ? "border-primary bg-primary/5"
              : "border-border-secondary"
          } ${canAutoCharge ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
        >
          <input
            type="radio"
            name="collection-method"
            checked={isChargeNow}
            onChange={() => onSelect("charge_card", "charge_automatically")}
            disabled={!canAutoCharge}
            className="mt-0.5"
          />
          <span className="text-content-primary">
            Charge card on file
            <span className="block text-xs text-content-tertiary font-normal">
              {pm
                ? `Bills ${pm.brand} •••• ${pm.last4} immediately.`
                : "No card on file for this client."}
            </span>
          </span>
        </label>
        <label
          className={`flex items-start gap-2 text-sm rounded-lg border p-3 ${
            isScheduleNotice
              ? "border-primary bg-primary/5"
              : "border-border-secondary"
          } ${
            canScheduleNotice
              ? "cursor-pointer"
              : "opacity-50 cursor-not-allowed"
          }`}
        >
          <input
            type="radio"
            name="collection-method"
            checked={isScheduleNotice}
            onChange={() => onSelect("schedule_notice", "charge_automatically")}
            disabled={!canScheduleNotice}
            className="mt-0.5"
          />
          <span className="text-content-primary flex-1">
            Email invoice, then auto-charge
            <span className="block text-xs text-content-tertiary font-normal">
              {canScheduleNotice
                ? `Nothing is charged now. We email the invoice today and charge the card on file in ${noticeDaysLabel}.`
                : canSendInvoice
                  ? "No card on file — this option ends in a card charge, so it needs one."
                  : "Link a Stripe customer first."}
            </span>
            {isScheduleNotice && (
              <span
                className="mt-2 flex items-center gap-1.5 text-xs text-content-primary font-normal"
                onClick={(e) => e.preventDefault()}
              >
                Auto-charge in
                <input
                  type="number"
                  min={1}
                  max={90}
                  value={noticeDaysStr}
                  onChange={(e) => onNoticeDaysChange(e.target.value)}
                  className={`w-16 px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                    isNoticeDaysValid
                      ? "border-border-secondary"
                      : "border-red-400"
                  }`}
                />
                days
                {!isNoticeDaysValid && (
                  <span className="text-red-600">1–90</span>
                )}
              </span>
            )}
          </span>
        </label>
        <label
          className={`flex items-start gap-2 text-sm rounded-lg border p-3 ${
            isSendInvoice
              ? "border-primary bg-primary/5"
              : "border-border-secondary"
          } ${canSendInvoice ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
        >
          <input
            type="radio"
            name="collection-method"
            checked={isSendInvoice}
            onChange={() => onSelect("charge_card", "send_invoice")}
            disabled={!canSendInvoice}
            className="mt-0.5"
          />
          <span className="text-content-primary flex-1">
            Email invoice — customer pays online
            <span className="block text-xs text-content-tertiary font-normal">
              {canSendInvoice
                ? "Nothing is charged now and no card is taken later. The customer pays via a Stripe payment link; the row shows Invoiced until they do."
                : "Link a Stripe customer first."}
            </span>
            {isSendInvoice && (
              <span
                className="mt-2 flex items-center gap-1.5 text-xs text-content-primary font-normal"
                onClick={(e) => e.preventDefault()}
              >
                Due in
                <input
                  type="number"
                  min={1}
                  max={90}
                  value={dueDaysStr}
                  onChange={(e) => onDueDaysChange(e.target.value)}
                  className={`w-16 px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                    isDueDaysValid
                      ? "border-border-secondary"
                      : "border-red-400"
                  }`}
                />
                days
                {!isDueDaysValid && (
                  <span className="text-red-600">1–90</span>
                )}
              </span>
            )}
          </span>
        </label>
      </div>
    </div>
  );
}
